// Enhanced ProductRow component with fixed discount calculation
import React from "react";
import StatusBadge from "./StatusBadge";
import DiscountBadge from "./DiscountBadge";
import DiscountInfo from "./DiscountInfo";
import ProductActions from "./ProductActions";
import { useStoreSettings } from "../../../../../hooks/useStoreSettings";
import { getLocalizedProductName } from "../../../../../utils/multilingualUtils";
import { useLanguage } from "../../../../../context/LanguageContext";
import { useAdminProductsTranslation } from "../../translations";

const ProductRow = ({
    product,
    isSelected,
    onSelect,
    onEdit,
    onDelete,
    onAddDiscount,
    onRemoveDiscount,
    onDirectRemoveDiscount, // New prop for direct discount removal
    onManageDiscounts, // New prop
    getProductDiscount, // Keep for backward compatibility
    getProductDiscounts, // New function for multiple discounts
    calculateDiscountedPrice,
    salesItems // Add for debugging
}) => {
    const { language } = useLanguage();
    const { t } = useAdminProductsTranslation(language);

    // Use the enhanced function if available, otherwise fall back to old one
    const productDiscounts = getProductDiscounts
        ? getProductDiscounts(product.id)
        : (getProductDiscount(product.id) ? [getProductDiscount(product.id)] : []);

    const primaryDiscount = productDiscounts.find(d => !d.variantId) || productDiscounts[0];


    // Check specifically for variant discounts
    const variantDiscounts = productDiscounts.filter(d => d.variantId);
    // console.log(`   - Variant discounts:`, variantDiscounts);

    // FIXED: Better discount price calculation
    const getDiscountedPrice = () => {
        if (!primaryDiscount) return null;

        // Handle both discount data structures
        const discountType = primaryDiscount.discountType || primaryDiscount.type;
        const discountValue = primaryDiscount.discountValue || primaryDiscount.value;
        const originalPrice = primaryDiscount.originalPrice || product.price;

        // Ensure we have valid numbers
        const price = parseFloat(originalPrice) || 0;
        const value = parseFloat(discountValue) || 0;

        //console.log(`   - Calculating price: ${price} with ${discountType} ${value}`);

        if (discountType === "percentage") {
            return price * (1 - value / 100);
        } else {
            return Math.max(0, price - value);
        }
    };

    const { currency: storeCurrency } = useStoreSettings();
    const discountedPrice = primaryDiscount ? getDiscountedPrice() : null;

    return (
        <tr className={productDiscounts.length > 0 ? "has-discount" : ""}>
            <td>
                <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={onSelect}
                />
            </td>
            <td className="product-cell">
                <div className="product-info">
                    <div className="admin-product-image">
                        {product.images && product.images.length > 0 ? (
                            <img src={product.images[0]} alt={getLocalizedProductName(product, 'en')} />
                        ) : product.imageUrl ? (
                            <img src={product.imageUrl} alt={getLocalizedProductName(product, 'en')} />
                        ) : (
                            <div className="no-image">{t('noImage')}</div>
                        )}
                        <DiscountBadge discount={primaryDiscount} currency={storeCurrency} />
                    </div>
                    <div className="product-name-container">
                        <span className="product-name">{getLocalizedProductName(product, 'en')}</span>
                        {/* Show variant count if product has variants */}
                        {product.variants && product.variants.length > 0 && (
                            <span className="variant-count">
                                {product.variants.length} {t('variants')}
                            </span>
                        )}
                    </div>
                </div>
            </td>
            <td>{product.category || t('uncategorized')}</td>
            <td>
                {/* Show total quantity across all variants or main product quantity */}
                {parseInt(product.quantity) === -1 ? (
                    <span style={{ fontSize: '18px', color: '#2e7d32', fontWeight: 'bold' }}>∞</span>
                ) : (
                    product.quantity || "0"
                )}
            </td>
            <td>
                <StatusBadge status={product.status || "Draft"} />
            </td>
            <td className="discount-cell">
                <DiscountInfo productDiscounts={productDiscounts} />
            </td>
            <td className="actions-cell">
                <ProductActions
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onAddDiscount={() => onAddDiscount(product)}
                    onDirectRemoveDiscount={onDirectRemoveDiscount}
                    hasDiscount={productDiscounts.length > 0}
                    hasVariants={product.variants && product.variants.length > 0}
                    productDiscounts={productDiscounts}
                />
            </td>
        </tr>
    );
};

export default ProductRow; 